import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Card,
    Col,
    Container,
    Input,
    Row,
    Button
} from "reactstrap";
import { API } from "../../api/API";
import { getData } from "../../CommonElements/Functions/PostGetFun";
import { ensureDexieReady, getFilteredPositionIds } from "../../db/DexieServises";
import CardHeaderSpan from "../../Utils/CommonComponents/CardHeaderSpan";
import TreeModel from "../Dashboards/Attendance/AttendanceTable/TreeModel";
import CommonTableNew from "../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew";
import DatePicker from 'react-datepicker';
import { Calendar, Download, Search } from 'react-feather';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux-toolkit/store';
import { dynamicImage } from "../../Utils";
import { Image } from "../../AbstractElements";
import 'react-datepicker/dist/react-datepicker.css';
import MonthDropdown from "../CommonCompo/MonthDropdown";
import { useParams } from "react-router-dom";

// Define the configuration for each report
const reportConfigs: Record<string, any> = {
    'attendance-register-details': {
        title: "Attendance Register Details Report",
        api: API.PendingAttendanceReport,
        usePositionFilter: false,
        useDateFilter: false,
        params: []
    },
    'employee-activity-performance': {
        title: "Employee Activity Performance Report",
        api: API.getDarReport,
        usePositionFilter: true,
        useDateFilter: true,
        params: ['date', 'position_id'],
        // visibleFields: ["DcrId","Agendacontroltype"]
    },
    'product-wise-support': {
        title: "Product wise support Report",
        api: API.getSupportCaptureSummary,
        usePositionFilter: true,
        useMonthFilter: true,
        params: ['moye', 'position_id']
    },
    'primary-vs-secondary': {
        title: "Primary Vs Secondary Report",
        api: "api/getPrimarySecondaryStockistReport",
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['moye', 'org_unit_id']
    },
    'visit-activity': {
        title: "Visit Activity Report",
        api: "api/reports/visitActivityReport",
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['moye']
    },
    'sample-report': {
        title: "Sample Report",
        api: API.getSgpiOutReport,
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['moye', 'position_id']
    },
    'call-average': {
        title: "Call Average Report",
        api: "api/getCallAverageReport",
        usePositionFilter: false,
        useDateRangeFilter: true,
        params: ['start_date', 'end_date']
    },
    'target-achievement': {
        title: "Target Achievement Report",
        api: "api/getTargetAchievementReport",
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['org_unit_id', 'moye']
    },
    'employee-claim': {
        title: "Employee Claim Report",
        api: "api/getEmployeeClaimReport",
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['moye', 'org_unit_id']
    },
    'rcpa-report': {
        title: "RCPA Report",
        api: API.getRcpaSkuSummary,
        usePositionFilter: true,
        useMonthFilter: true,
        params: ['moye', 'position_id']
    },
    'pending-dcr-report': {
        title: "Pending DCR Report",
        api: API.pendingDcrReport,
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['moye']
    },
    'get-Mas-Report-V2': {
        title: "Mass Report V2",
        api: API.getMasReportV2,
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['moye', 'org_unit_id']
    },
    'get-Dar-Report-V2': {
        title: "Dar Report V2",
        api: API.getDarReportV2,
        usePositionFilter: false,
        useDateRangeFilter: true,
        params: ['start_date', 'end_date']
    },
    'get-Sgpi-Out-Report-V2': {
        title: "Sgpi Out Report V2",
        api: API.getSgpiOutReportV2,
        usePositionFilter: false,
        useDateRangeFilter: true,
        params: ['start_date', 'end_date']
    },
    'expenses-report': {
        title: "Expenses Report",
        api: "api/getExpensesReport",
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['moye', 'org_unit_id']
    },
    'expenses-summary': {
        title: "Expenses Summary Report",
        api: "api/getExpensesSummary",
        usePositionFilter: false,
        useMonthFilter: true,
        params: ['moye', 'org_unit_id']
    }
};

const CommonReport = () => {
    const { reportType } = useParams<{ reportType: string }>();
    const config = reportConfigs[reportType || ''];

    const [searchText, setSearchText] = useState('');
    const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
    const [reportData, setReportData] = useState<any[]>([]);
    const [displayedData, setDisplayedData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState<Date | null>(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d;
    });
    const [endDate, setEndDate] = useState<Date | null>(new Date());
    const [selectedMoye, setSelectedMoye] = useState<string>(''); // MM-YYYY

    const serverUrl = localStorage.getItem('serverUrl');
    const APP_TOKEN = localStorage.getItem('appToken');
    const unitId = useSelector((state: RootState) => state.orgUnit.id);
    const datePickerRef = useRef<any>(null);

    const handleContainerClick = () => {
        if (datePickerRef.current) {
            datePickerRef.current.setFocus();
        }
    };

    const fetchData = async () => {
        if (!config || !serverUrl || !APP_TOKEN || !unitId) return;
        setLoading(true);

        try {
            await ensureDexieReady();
            const dexieIds = await getFilteredPositionIds('Position', unitId);
            const positionIds = selectedIds.length > 0 ? selectedIds : (dexieIds || []);

            const payload: any = {};

            if (config.params.includes('position_id')) {
                payload.position_id = positionIds;
            }
            if (config.params.includes('date')) {
                const date = startDate || new Date();
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, '0');
                const d = String(date.getDate()).padStart(2, '0');
                payload.date = `${y}-${m}-${d}`;
            }
            if (config.params.includes('start_date')) {
                const date = startDate || new Date();
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, '0');
                const d = String(date.getDate()).padStart(2, '0');
                payload.start_date = `${y}-${m}-${d}`;
            }
            if (config.params.includes('end_date')) {
                const date = endDate || new Date();
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, '0');
                const d = String(date.getDate()).padStart(2, '0');
                payload.end_date = `${y}-${m}-${d}`;
            }
            if (config.params.includes('moye')) {
                payload.moye = selectedMoye;
            }
            if (config.params.includes('org_unit_id')) {
                payload.org_unit_id = unitId;
            }

            const url = `${serverUrl}${config.api}`;
            const { status, data } = await getData(url, true, payload);

            if (status === 200 && (Array.isArray(data?.data) || Array.isArray(data))) {
                setReportData(data?.data || data);
            } else {
                setReportData([]);
            }
        } catch (error) {
            console.error("Error fetching report data:", error);
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (config) {
            // Only fetch if moye is set (if required)
            if (config.useMonthFilter && !selectedMoye) return;
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportType, startDate, endDate, selectedMoye, selectedIds, unitId]);

    useEffect(() => {
        const lower = searchText.toLowerCase();
        const filtered = reportData.filter((item) => {
            if (!item) return false;
            return Object.values(item).some(val =>
                String(val).toLowerCase().includes(lower)
            );
        });
        setDisplayedData(filtered);
    }, [searchText, reportData]);

    const keys: string[] = useMemo(() => {
        if (!reportData.length || !reportData[0]) return [];

        // If visibleFields is specified, use that exact list and order
        if (config.visibleFields && Array.isArray(config.visibleFields)) {
            return config.visibleFields as string[];
        }

        // Otherwise, get all keys from the first data item
        let allKeys = Object.keys(reportData[0]);

        // Filter out excluded fields if specified
        if (config.excludeFields && Array.isArray(config.excludeFields)) {
            const exclude = config.excludeFields as string[];
            allKeys = allKeys.filter(key => !exclude.includes(key));
        }

        return allKeys;
    }, [reportData, config]);

    const captionTableHeader = useMemo(() => {
        return keys.map((key: string, index: number) => ({
            id: index.toString(),
            head: key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim().toUpperCase(),
            key: key
        }));
    }, [keys]);

    const renderCellValue = (value: any): React.ReactNode => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';

        // Array of objects (e.g. remarks/activity list)
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
            const subKeys = Object.keys(value[0]);
            return (
                <div style={{ minWidth: '260px' }}>
                    {value.map((item: any, i: number) => (
                        <div
                            key={i}
                            style={{
                                background: i % 2 === 0 ? 'rgba(var(--bs-primary-rgb), 0.06)' : 'rgba(var(--bs-secondary-rgb), 0.04)',
                                borderLeft: '3px solid var(--bs-primary)',
                                borderRadius: '6px',
                                padding: '5px 8px',
                                marginBottom: i < value.length - 1 ? '5px' : 0,
                                fontSize: '0.78rem',
                            }}
                        >
                            {subKeys.map((sk) => (
                                <div key={sk} style={{ display: 'flex', gap: '4px', alignItems: 'flex-start', lineHeight: '1.4' }}>
                                    <span style={{ fontWeight: 600, color: 'var(--bs-primary)', whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                                        {sk.replace(/_/g, ' ')}:
                                    </span>
                                    <span style={{ color: 'var(--bs-body-color)' }}>
                                        {item[sk] ?? '-'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            );
        }

        // Plain array of primitives
        if (Array.isArray(value)) {
            return value.join(', ') || '-';
        }

        // Plain object
        if (typeof value === 'object') {
            return (
                <div style={{ fontSize: '0.78rem' }}>
                    {Object.entries(value).map(([k, v]) => (
                        <div key={k} style={{ display: 'flex', gap: '4px' }}>
                            <span style={{ fontWeight: 600, color: 'var(--bs-primary)', textTransform: 'capitalize' }}>
                                {k.replace(/_/g, ' ')}:
                            </span>
                            <span>{String(v)}</span>
                        </div>
                    ))}
                </div>
            );
        }

        return value;
    };

    const handleExportToExcel = () => {
        if (displayedData.length === 0) return;

        const worksheet = XLSX.utils.json_to_sheet(displayedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, `${config?.title || 'Report'}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    if (!config) {
        return <div className="p-4">Report not found.</div>;
    }

    return (
        <div className='page-body p-2'>
            <Card>
                <div className='d-flex justify-content-between align-items-center pt-2 pb-2 px-3'>
                    <CardHeaderSpan
                        headingClassName='pb-0'
                        back={true}
                        heading={config.title}
                        span="Report"
                    />

                    {config.useMonthFilter ? (
                        <div className="p-1">
                            <MonthDropdown onChange={(moye) => setSelectedMoye(moye)} />
                        </div>
                    ) : config.useDateFilter ? (
                        <div
                            onClick={handleContainerClick}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                width: '200px',
                                border: 'none',
                                borderRadius: '5px',
                                padding: '6px 12px',
                                cursor: 'pointer'
                            }}
                            className="bg-primary"
                        >
                            <Calendar size={18} color="#fff" />
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                maxDate={new Date()}
                                dateFormat="yyyy-MM-dd"
                                className="form-control border-0 p-0 text-light bg-transparent"
                                wrapperClassName="w-100"
                                ref={datePickerRef}
                            />
                        </div>
                    ) : config.useDateRangeFilter && (
                        <div className="d-flex align-items-center gap-2">
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    width: '180px',
                                    border: 'none',
                                    borderRadius: '5px',
                                    padding: '6px 12px',
                                    cursor: 'pointer'
                                }}
                                className="bg-primary"
                            >
                                <Calendar size={18} color="#fff" />
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    maxDate={endDate || new Date()}
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="Start Date"
                                    className="form-control border-0 p-0 text-light bg-transparent"
                                    wrapperClassName="w-100"
                                />
                            </div>
                            <span className="text-muted">to</span>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    width: '180px',
                                    border: 'none',
                                    borderRadius: '5px',
                                    padding: '6px 12px',
                                    cursor: 'pointer'
                                }}
                                className="bg-primary"
                            >
                                <Calendar size={18} color="#fff" />
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    minDate={startDate || undefined}
                                    maxDate={new Date()}
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="End Date"
                                    className="form-control border-0 p-0 text-light bg-transparent"
                                    wrapperClassName="w-100"
                                />
                            </div>
                        </div>
                    )}
                </div>

                <Container fluid className="mt-3">
                    <Row className="mb-3 align-items-center justify-content-between">
                        <Col md={8} className="d-flex align-items-center gap-3 flex-wrap">
                            <div className="position-relative" style={{ maxWidth: "300px", width: "100%" }}>
                                <Input
                                    type="text"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    placeholder="Search in report..."
                                    className="form-control ps-4"
                                />
                                <Search size={14} className="position-absolute translate-middle-y top-50 start-0 ms-2 text-muted" />
                            </div>
                            {config.usePositionFilter && (
                                <TreeModel onSelectionChange={setSelectedIds} />
                            )}
                        </Col>

                        <Col md={4} className="d-flex justify-content-end gap-3">
                            <Button
                                color="primary"
                                size="sm"
                                className="d-flex align-items-center gap-2"
                                onClick={handleExportToExcel}
                                disabled={displayedData.length === 0}
                            >
                                <Download size={14} />
                                <span>Export</span>
                            </Button>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={12}>
                            <div className="table-responsive" style={{ minHeight: '400px' }}>
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Image
                                            className="b-r-10 img-80"
                                            src={dynamicImage('dashboard/loader.gif')}
                                            alt="Loading..."
                                        />
                                    </div>
                                ) : (
                                    <CommonTableNew
                                        caption=""
                                        headData={captionTableHeader}
                                        data={displayedData}
                                        renderRow={(row, rowIndex) => {
                                            if (!row) return <tr key={rowIndex}><td colSpan={keys.length}>-</td></tr>;
                                            return (
                                                <tr key={rowIndex}>
                                                    {keys.map((key, colIndex) => (
                                                        <td key={colIndex} style={{ verticalAlign: 'top' }}>
                                                            {renderCellValue(row[key])}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        }}
                                    />
                                )}
                                {!loading && displayedData.length === 0 && (
                                    <div className="text-center py-4 text-muted">
                                        No data found for the selected criteria.
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </Card>
        </div>
    );
};

export default CommonReport;
